﻿/**
 * Covermatic Studio gallery controller.
 */
(() => {
  const runtime = window.CovermaticRuntime || window.AlphaRuntime || {};
  window.CovermaticRuntime = runtime;
  window.AlphaRuntime = runtime;
  const { Config, State, Utils, Events } = runtime;
  const serializeGalleryLog = (value) => {
    if (value instanceof Error) return { name: value.name, message: value.message, stack: value.stack };
    if (value && typeof value === 'object') {
      const out = {};
      Object.keys(value).forEach((key) => {
        const item = value[key];
        out[key] = item instanceof Error ? { name: item.name, message: item.message, stack: item.stack } : item;
      });
      return out;
    }
    return value;
  };
  const logGallery = (...args) => console.info('[Covermatic gallery]', ...args.map(serializeGalleryLog));
  const warnGallery = (...args) => console.warn('[Covermatic gallery]', ...args.map(serializeGalleryLog));
  const errorGallery = (...args) => console.error('[Covermatic gallery]', ...args.map(serializeGalleryLog));

  const extractFileNameFromUrl = (rawUrl) => {
    const value = String(rawUrl || '').trim();
    if (!value) return null;

    try {
      const parsed = new URL(value, window.location.origin);
      const lastSegment = parsed.pathname.split('/').filter(Boolean).pop() || '';
      return lastSegment ? decodeURIComponent(lastSegment) : null;
    } catch (_error) {
      const cleaned = value.split('?')[0].split('#')[0];
      const lastSegment = cleaned.split('/').filter(Boolean).pop() || '';
      return lastSegment ? decodeURIComponent(lastSegment) : null;
    }
  };

  class GalleryController {
    constructor() {
      this.grid = Utils.$('galleryGrid');
      this.sentinel = Utils.$('fetchSentinel');
      this.loading = Utils.$('fetchLoading');
      this.end = Utils.$('fetchEnd');
      this.activeFetchPromise = null;
      this.initialFetchTimer = null;
      this.lineageCache = new Map();
      this.recordsByUrl = new Map();
      this.recordsByFileName = new Map();
      this.recordsById = new Map();
      this.unsubscribers = [];

      this.init();
    }

    init() {
      this.unsubscribers.push(
        Events.on('gallery:prepend-images', ({ detail }) => this.prependImages(detail.images)),
        Events.on('gallery:apply-edit', ({ detail }) => this.applyEdit(detail.image)),
      );

      if (!Utils.isLoggedIn()) {
        this.renderGuestGallery();
      } else {
        this.scheduleInitialFetch();

        const obs = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting) this.fetchNextPage();
        }, { rootMargin: '400px' });
        if (this.sentinel) obs.observe(this.sentinel);
      }

      document.addEventListener('click', (event) => {
        const action = event.target.closest('[data-card-action]');
        const card = event.target.closest('[data-covermatic-gallery-card][data-url], button[data-url]');
        if (!card?.dataset.url) return;
        const record = this.recordFromCard(card);
        if (!record) return;

        if (action?.dataset.cardAction === 'share') {
          event.preventDefault();
          event.stopPropagation();
          if (record.isFreeGeneration === true) {
            Utils.toast('Free cover previews cannot be shared for credits.', 'warn');
            return;
          }
          Events.emit('artwork:share-requested', { record });
          return;
        }

        Events.emit('modal:image-open-requested', { record });
      });

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        const card = event.target.closest('[data-covermatic-gallery-card][data-url]');
        if (!card?.dataset.url || event.target.closest('[data-card-action]')) return;
        event.preventDefault();
        const record = this.recordFromCard(card);
        if (record) Events.emit('modal:image-open-requested', { record });
      });
    }

    scheduleInitialFetch() {
      const run = () => {
        this.initialFetchTimer = null;
        this.fetchNextPage().catch((error) => {
          errorGallery('Initial gallery fetch failed', { error });
        });
      };

      this.initialFetchTimer = window.setTimeout(run, 250);
    }

    renderGuestGallery() {
      const container = Utils.$('guestExamplesContainer');
      if (container) {
        Config.guestImages.forEach((img) => {
          const btn = document.createElement('button');
          btn.className = 'covermatic-gallery-card covermatic-gallery-card--guest group relative flex-none w-40 md:w-60 snap-center text-left';
          btn.dataset.url = img.URL;
          btn.innerHTML = `
            <div class="covermatic-gallery-card__frame">
              <div class="covermatic-gallery-card__media">
                <img src="${img.URL}" class="covermatic-gallery-card__image" loading="lazy" decoding="async" />
              </div>
            </div>
          `;
          container.appendChild(btn);
        });
        this.initMarquee('guest-examples-container');
      }

      this.initMarquee('guest-premade-container');
    }

    initMarquee(id) {
      const el = Utils.$(`#${id}`);
      if (!el || !el.children.length) return;

      el.style.overflow = 'hidden';
      const track = document.createElement('div');
      track.style.cssText = 'display:flex;gap:1rem;width:max-content;will-change:transform;';

      while (el.firstChild) track.appendChild(el.firstChild);
      el.appendChild(track);
      [...track.children].forEach((child) => track.appendChild(child.cloneNode(true)));

      const width = track.scrollWidth / 2;
      const duration = width / 50;
      const style = document.createElement('style');
      style.textContent = `@keyframes marquee-${id} { 0%{transform:translateX(0)} 100%{transform:translateX(-${width}px)} }`;
      document.head.appendChild(style);
      track.style.animation = `marquee-${id} ${duration}s linear infinite`;
      el.onmouseenter = () => { track.style.animationPlayState = 'paused'; };
      el.onmouseleave = () => { track.style.animationPlayState = 'running'; };
    }

    async fetchNextPage() {
      if (this.activeFetchPromise) return this.activeFetchPromise;
      if (State.isFetching || !State.hasMore) return { added: 0, hasMore: State.hasMore, skipped: true };

      this.activeFetchPromise = (async () => {
        State.isFetching = true;
        this.loading?.classList.remove('hidden');
        let added = 0;

        try {
          const data = await this.fetchHistoricalPage();
          const images = this.extractRecords(data);
          logGallery('Fetched gallery page', {
            page: State.page,
            count: images.length,
            keys: data && typeof data === 'object' && !Array.isArray(data) ? Object.keys(data) : [],
          });

          images.forEach((item) => {
            const record = this.resolveRecord(item);
            if (record?.url && !State.urlSet.has(record.url) && this.grid) {
              State.urlSet.add(record.url);
              this.grid.appendChild(this.createCard(record));
              added += 1;
            }
          });

          State.hasMore = this.resolveHasMore(data, images.length);
          State.page += 1;
          if (!State.hasMore) this.end?.classList.remove('hidden');

          return { added, hasMore: State.hasMore };
        } catch (error) {
          errorGallery('Failed to fetch gallery page', { page: State.page, error });
          return { added, hasMore: State.hasMore, error: true };
        } finally {
          State.isFetching = false;
          this.loading?.classList.add('hidden');
          this.activeFetchPromise = null;
        }
      })();

      return this.activeFetchPromise;
    }

    async fetchHistoricalPage() {
      const apiBase = String(Config.apiBase || '/').replace(/\/+$/, '');
      const historicalQuery = new URLSearchParams({
        customerId: Config.customerId || '',
        limit: String(Config.perFetch),
        offset: String(State.page * Config.perFetch),
      });
      ApiClient.appendToolContext?.(historicalQuery);
      const historicalCandidates = [
        `${apiBase}/app_proxy/historical-generations?${historicalQuery}`,
        `${apiBase}/api/historical-generations?${historicalQuery}`,
        `${apiBase}/historical-generations?${historicalQuery}`,
      ];
      logGallery('Requesting historical generations', {
        page: State.page,
        candidates: historicalCandidates,
      });

      const historicalFailures = [];
      for (const historicalUrl of historicalCandidates) {
        const historicalResponse = await Utils.withTimeout(
          historicalUrl,
          { credentials: 'same-origin' },
          8000,
        );
        logGallery('Historical generations response', {
          page: State.page,
          url: historicalUrl,
          status: historicalResponse.status,
          ok: historicalResponse.ok,
        });
        if (historicalResponse.ok) {
          const data = await historicalResponse.json();
          logGallery('Historical generations payload received', {
            page: State.page,
            url: historicalUrl,
            type: Array.isArray(data) ? 'array' : typeof data,
          });
          return data;
        }

        historicalFailures.push({
          url: historicalUrl,
          status: historicalResponse.status,
        });

        if (historicalResponse.status !== 401 && historicalResponse.status !== 404) {
          const body = await historicalResponse.text().catch(() => '');
          throw new Error(`Historical generations request failed with ${historicalResponse.status} for ${historicalUrl}: ${body.slice(0, 240)}`);
        }
      }

      const legacyQuery = new URLSearchParams({
        customerId: Config.customerId,
        start: String(State.page * Config.perFetch),
        count: String(Config.perFetch),
      });
      const legacyUrl = `${apiBase}/images?${legacyQuery}`;
      warnGallery('Falling back to legacy images endpoint', {
        page: State.page,
        historicalFailures,
        legacyUrl,
      });
      const legacyResponse = await Utils.withTimeout(
        legacyUrl,
        { credentials: 'same-origin' },
        8000,
      );
      logGallery('Legacy images response', {
        page: State.page,
        status: legacyResponse.status,
        ok: legacyResponse.ok,
      });
      if (!legacyResponse.ok) {
        const body = await legacyResponse.text().catch(() => '');
        throw new Error(`Legacy images request failed with ${legacyResponse.status} for ${legacyUrl}: ${body.slice(0, 240)}`);
      }

      const data = await legacyResponse.json();
      logGallery('Legacy images payload received', {
        page: State.page,
        type: Array.isArray(data) ? 'array' : typeof data,
      });
      warnGallery('Legacy images payload has no lineage metadata; modal navigation will be degraded until a historical endpoint succeeds', {
        page: State.page,
      });
      return data;
    }

    extractRecords(data) {
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.images)) return data.images;
      if (Array.isArray(data?.items)) return data.items;
      if (Array.isArray(data?.rows)) return data.rows;
      if (Array.isArray(data?.generations)) return data.generations;
      if (Array.isArray(data?.data)) return data.data;
      return [];
    }

    resolveHasMore(data, count) {
      if (typeof data?.pagination?.has_more === 'boolean') return data.pagination.has_more;
      if (typeof data?.has_more === 'boolean') return data.has_more;
      return count >= Config.perFetch;
    }

    normalizeRecord(item) {
      if (!item) return null;
      if (typeof item === 'string') {
        const fileName = extractFileNameFromUrl(item);
        return {
          id: null,
          url: item,
          fileName,
          uniqueFileName: fileName,
          editId: null,
          parentCoverId: null,
          coverGroupId: null,
          lineageDepth: null,
          createdAt: null,
          artistName: '',
          songName: '',
          coverArtIdea: '',
          status: '',
          isEdit: false,
          assetFamily: null,
          assetType: null,
          sizePreset: null,
          isFreeGeneration: false,
          hasFreeWatermark: false,
          freeQuotaSource: '',
          logoFreeSourceUrl: '',
          conversionType: '',
          officializedFromCoverArtId: null,
          sharedWithDsgfx: false,
          shareCreditsAwarded: 0,
        };
      }

      const rawUrl = item.image_url || item.url || item.URL || '';
      const url = typeof rawUrl === 'string' ? rawUrl.trim() : '';
      if (!url) return null;

      const normalizeNumber = (value) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
      };

      const fileName = String(item.file_name || item.unique_file_name || '').trim() || extractFileNameFromUrl(url);
      const logs = item.logs && typeof item.logs === 'object' ? item.logs : {};
      const isFreeGeneration = item.is_free_generation === true || item.isFreeGeneration === true || logs.is_free_generation === true;
      const hasFreeWatermark = item.has_free_watermark === true || item.hasFreeWatermark === true || logs.has_free_watermark === true;

      return {
        id: normalizeNumber(item.id),
        url,
        fileName,
        uniqueFileName: fileName,
        editId: String(item.edit_id || '').trim() || null,
        parentCoverId: normalizeNumber(item.parent_cover_id),
        coverGroupId: normalizeNumber(item.cover_group_id),
        lineageDepth: normalizeNumber(item.lineage_depth),
        createdAt: String(item.created_at || '').trim() || null,
        artistName: String(item.artist_name || '').trim(),
        songName: String(item.song_name || '').trim(),
        coverArtIdea: String(item.cover_art_idea || item.coverArtIdea || logs.cover_art_idea || '').trim(),
        status: String(item.status || '').trim(),
        isEdit: item.is_edit === true,
        assetFamily: String(item.asset_family || '').trim() || null,
        assetType: String(item.asset_type || '').trim() || null,
        toolSlug: String(item.tool_slug || logs.tool_slug || '').trim() || null,
        nicheSlug: String(item.niche_slug || logs.niche_slug || '').trim() || null,
        styleSlug: String(item.style_slug || logs.style_slug || '').trim() || null,
        styleName: String(item.style_name || logs.style_name || '').trim() || null,
        canvasReady: item.canvas_ready === true || logs.canvas_ready === true,
        sizePreset: String(item.size_preset || '').trim() || null,
        isFreeGeneration,
        hasFreeWatermark,
        freeQuotaSource: String(item.free_quota_source || item.freeQuotaSource || logs.free_quota_source || '').trim(),
        logoFreeSourceUrl: String(item.logo_free_source_url || item.logoFreeSourceUrl || logs.logo_free_source_url || '').trim(),
        conversionType: String(item.conversion_type || item.conversionType || logs.conversion_type || '').trim(),
        officializedFromCoverArtId: normalizeNumber(item.officialized_from_cover_art_id || item.officializedFromCoverArtId || logs.officialized_from_cover_art_id),
        sharedWithDsgfx: item.shared_with_dsgfx === true || item.sharedWithDsgfx === true,
        shareCreditsAwarded: normalizeNumber(item.share_credits_awarded || item.shareCreditsAwarded) || 0,
      };
    }

    mergeRecord(existing, normalized) {
      if (!existing) return normalized;
      const prefer = (nextValue, previousValue) => {
        if (nextValue === null || nextValue === undefined || nextValue === '') return previousValue ?? nextValue;
        return nextValue;
      };

      return {
        id: prefer(normalized.id, existing.id),
        url: prefer(normalized.url, existing.url),
        fileName: prefer(normalized.fileName, existing.fileName),
        uniqueFileName: prefer(normalized.uniqueFileName, existing.uniqueFileName),
        editId: prefer(normalized.editId, existing.editId),
        parentCoverId: prefer(normalized.parentCoverId, existing.parentCoverId),
        coverGroupId: prefer(normalized.coverGroupId, existing.coverGroupId),
        lineageDepth: prefer(normalized.lineageDepth, existing.lineageDepth),
        createdAt: prefer(normalized.createdAt, existing.createdAt),
        artistName: prefer(normalized.artistName, existing.artistName),
        songName: prefer(normalized.songName, existing.songName),
        coverArtIdea: prefer(normalized.coverArtIdea, existing.coverArtIdea),
        status: prefer(normalized.status, existing.status),
        isEdit: normalized.isEdit === true || existing.isEdit === true,
        assetFamily: prefer(normalized.assetFamily, existing.assetFamily),
        assetType: prefer(normalized.assetType, existing.assetType),
        toolSlug: prefer(normalized.toolSlug, existing.toolSlug),
        nicheSlug: prefer(normalized.nicheSlug, existing.nicheSlug),
        styleSlug: prefer(normalized.styleSlug, existing.styleSlug),
        styleName: prefer(normalized.styleName, existing.styleName),
        canvasReady: normalized.canvasReady === true || existing.canvasReady === true,
        sizePreset: prefer(normalized.sizePreset, existing.sizePreset),
        isFreeGeneration: normalized.isFreeGeneration === true || existing.isFreeGeneration === true,
        hasFreeWatermark: normalized.hasFreeWatermark === true || existing.hasFreeWatermark === true,
        freeQuotaSource: prefer(normalized.freeQuotaSource, existing.freeQuotaSource),
        logoFreeSourceUrl: prefer(normalized.logoFreeSourceUrl, existing.logoFreeSourceUrl),
        conversionType: prefer(normalized.conversionType, existing.conversionType),
        officializedFromCoverArtId: prefer(normalized.officializedFromCoverArtId, existing.officializedFromCoverArtId),
        sharedWithDsgfx: normalized.sharedWithDsgfx === true || existing.sharedWithDsgfx === true,
        shareCreditsAwarded: Math.max(Number(normalized.shareCreditsAwarded || 0), Number(existing.shareCreditsAwarded || 0)),
      };
    }

    resolveRecord(item) {
      const normalized = this.normalizeRecord(item);
      if (!normalized) return null;

      const existingByFileName = normalized.fileName ? this.recordsByFileName.get(normalized.fileName) : null;
      const existingByUrl = this.recordsByUrl.get(normalized.url);
      const existingById = normalized.id != null ? this.recordsById.get(normalized.id) : null;
      const existing = existingByFileName || existingById || existingByUrl;

      const record = this.mergeRecord(existing, normalized);

      if (record.url) this.recordsByUrl.set(record.url, record);
      if (record.fileName) this.recordsByFileName.set(record.fileName, record);
      if (record.id != null) this.recordsById.set(record.id, record);

      return record;
    }

    normalizeLineagePayload(data, fileName) {
      const rawSequence = Array.isArray(data?.sequence)
        ? data.sequence
        : Array.isArray(data?.rows)
          ? data.rows
          : Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data)
              ? data
              : [];
      const sequence = rawSequence
        .map((item) => this.resolveRecord(item))
        .filter(Boolean);
      const current = this.resolveRecord(data?.current) || sequence.find((record) => record.fileName === fileName) || null;
      const currentIndex = sequence.findIndex((record) => {
        if (current?.id != null && record.id != null) return record.id === current.id;
        if (current?.fileName && record.fileName) return record.fileName === current.fileName;
        return record.url === current?.url;
      });

      logGallery('Normalized lineage payload', {
        fileName,
        sequenceSize: sequence.length,
        currentFileName: current?.fileName || null,
        currentIndex,
        keys: data && typeof data === 'object' && !Array.isArray(data) ? Object.keys(data) : [],
      });
      return sequence;
    }

    async fetchLineage(fileName, options = {}) {
      if (!fileName) return [];
      const force = options && options.force === true;
      if (!force && this.lineageCache.has(fileName)) {
        const cachedSequence = this.lineageCache.get(fileName) || [];
        logGallery('Using cached server lineage sequence', {
          fileName,
          sequenceSize: cachedSequence.length,
        });
        return cachedSequence;
      }

      const apiBase = String(Config.apiBase || '/').replace(/\/+$/, '');
      const lineageQuery = new URLSearchParams({ fileName });
      ApiClient.appendToolContext?.(lineageQuery);
      const lineageCandidates = [
        `${apiBase}/app_proxy/cover-lineage?${lineageQuery}`,
        `${apiBase}/api/cover-lineage?${lineageQuery}`,
        `${apiBase}/cover-lineage?${lineageQuery}`,
      ];
      logGallery('Requesting server lineage', {
        fileName,
        candidates: lineageCandidates,
      });

      for (const lineageUrl of lineageCandidates) {
        const lineageResponse = await fetch(lineageUrl);
        logGallery('Server lineage response', {
          fileName,
          url: lineageUrl,
          status: lineageResponse.status,
          ok: lineageResponse.ok,
        });
        if (!lineageResponse.ok) {
          if (lineageResponse.status === 401 || lineageResponse.status === 404) continue;
          throw new Error(`Cover lineage request failed with ${lineageResponse.status} for ${lineageUrl}`);
        }

        const data = await lineageResponse.json();
        const sequence = this.normalizeLineagePayload(data, fileName);
        sequence.forEach((record) => {
          if (record.fileName) this.lineageCache.set(record.fileName, sequence);
        });
        if (!this.lineageCache.has(fileName)) this.lineageCache.set(fileName, sequence);
        return sequence;
      }

      warnGallery('Server lineage unavailable for file', { fileName });
      return [];
    }

    getGroupSequence(record) {
      const resolved = this.resolveRecord(record);
      if (!resolved?.coverGroupId) return [];

      const sequence = [...this.recordsByFileName.values()]
        .filter((entry) => entry.coverGroupId === resolved.coverGroupId)
        .sort((left, right) => {
          const leftDepth = left.lineageDepth ?? Number.MAX_SAFE_INTEGER;
          const rightDepth = right.lineageDepth ?? Number.MAX_SAFE_INTEGER;
          if (leftDepth !== rightDepth) return leftDepth - rightDepth;

          const leftTime = left.createdAt ? Date.parse(left.createdAt) : 0;
          const rightTime = right.createdAt ? Date.parse(right.createdAt) : 0;
          if (leftTime !== rightTime) return leftTime - rightTime;

          return (left.id ?? 0) - (right.id ?? 0);
        });

      logGallery('Built lineage group sequence', {
        coverGroupId: resolved.coverGroupId,
        activeFileName: resolved.fileName,
        activeDepth: resolved.lineageDepth,
        size: sequence.length,
      });
      return sequence;
    }

    recordFromCard(card) {
      return this.resolveRecord({
        url: card.dataset.url,
        file_name: card.dataset.fileName || '',
        unique_file_name: card.dataset.uniqueFileName || card.dataset.fileName || '',
        id: card.dataset.recordId || null,
        cover_group_id: card.dataset.coverGroupId || null,
        parent_cover_id: card.dataset.parentCoverId || null,
        lineage_depth: card.dataset.lineageDepth || null,
        is_free_generation: card.dataset.freeGeneration === 'true',
        has_free_watermark: card.dataset.freeWatermark === 'true',
        logo_free_source_url: card.dataset.logoFreeSourceUrl || '',
        conversion_type: card.dataset.conversionType || '',
        officialized_from_cover_art_id: card.dataset.officializedFromCoverArtId || null,
        shared_with_dsgfx: card.dataset.sharedWithDsgfx === 'true',
        share_credits_awarded: card.dataset.shareCreditsAwarded || 0,
      });
    }

    aspectRatioForPreset(sizePreset) {
      const normalized = String(sizePreset || '').trim().toLowerCase();
      const ratioMap = {
        square_1_1: '1 / 1',
        landscape_3_2: '3 / 2',
        portrait_2_3: '2 / 3',
        portrait_3_4: '3 / 4',
        landscape_4_3: '4 / 3',
        portrait_4_5: '4 / 5',
        landscape_5_4: '5 / 4',
        story_9_16: '9 / 16',
        banner_16_9: '16 / 9',
        ultrawide_21_9: '21 / 9',
      };
      return ratioMap[normalized] || '4 / 5';
    }

    createCard(record) {
      const card = document.createElement('article');
      card.className = 'covermatic-gallery-card group block w-full text-left';
      card.style.setProperty('--covermatic-card-aspect-ratio', this.aspectRatioForPreset(record.sizePreset));
      card.dataset.covermaticGalleryCard = 'true';
      card.dataset.url = record.url;
      card.dataset.sharedWithDsgfx = record.sharedWithDsgfx ? 'true' : 'false';
      card.dataset.shareCreditsAwarded = String(record.shareCreditsAwarded || 0);
      card.setAttribute('role', 'button');
      card.setAttribute('tabindex', '0');
      if (record.fileName) {
        card.dataset.fileName = record.fileName;
        card.dataset.uniqueFileName = record.uniqueFileName || record.fileName;
      }
      if (record.id != null) card.dataset.recordId = String(record.id);
      if (record.sizePreset) card.dataset.sizePreset = record.sizePreset;
      if (record.isFreeGeneration) card.dataset.freeGeneration = 'true';
      if (record.hasFreeWatermark) card.dataset.freeWatermark = 'true';
      if (record.logoFreeSourceUrl) card.dataset.logoFreeSourceUrl = record.logoFreeSourceUrl;
      if (record.conversionType) card.dataset.conversionType = record.conversionType;
      if (record.officializedFromCoverArtId != null) card.dataset.officializedFromCoverArtId = String(record.officializedFromCoverArtId);
      if (record.coverGroupId != null) card.dataset.coverGroupId = String(record.coverGroupId);
      if (record.parentCoverId != null) card.dataset.parentCoverId = String(record.parentCoverId);
      if (record.lineageDepth != null) card.dataset.lineageDepth = String(record.lineageDepth);
      const shareLabel = record.sharedWithDsgfx ? 'Shared +2' : 'Share +2';
      const shareAction = record.isFreeGeneration ? '' : `
            <span aria-hidden="true">&bull;</span>
            <button type="button" class="covermatic-gallery-card__action covermatic-gallery-card__share" data-card-action="share" ${record.sharedWithDsgfx ? 'disabled' : ''}>${shareLabel}</button>`;
      card.innerHTML = `
        <div class="covermatic-gallery-card__frame">
          <div class="covermatic-gallery-card__media">
            <img src="${Utils.escapeHtml(record.url)}" loading="lazy" decoding="async" class="covermatic-gallery-card__image" />
          </div>
          <div class="covermatic-gallery-card__footer" aria-label="Artwork actions">
            <button type="button" class="covermatic-gallery-card__action" data-card-action="edit">Edit</button>
            <span aria-hidden="true">&bull;</span>
            <button type="button" class="covermatic-gallery-card__action" data-card-action="download">Download</button>
            ${shareAction}
          </div>
        </div>
      `;
      return card;
    }

    prependImages(items) {
      const urls = Array.isArray(items) ? items : [items];
      urls.forEach((item) => {
        const record = this.resolveRecord(item);
        if (record?.url && !State.urlSet.has(record.url)) {
          State.urlSet.add(record.url);
          const card = this.createCard(record);
          if (this.grid) this.grid.prepend(card);
        }
      });
    }

    async applyEdit(urlObj) {
      let record = this.resolveRecord(urlObj);
      if (!record?.url) return;

      if (record.fileName) {
        try {
          const sequence = await this.fetchLineage(record.fileName, { force: true });
          const refreshedRecord = sequence.find((item) => {
            if (record.fileName && item.fileName) return item.fileName === record.fileName;
            return item.url === record.url;
          });
          if (refreshedRecord) record = this.resolveRecord(refreshedRecord) || refreshedRecord;
        } catch (error) {
          warnGallery('Failed to refresh edit lineage after completion', {
            fileName: record.fileName,
            url: record.url,
            error: error?.message || String(error),
          });
        }
      }

      State.currentUrl = record.url;
      State.currentImageRecord = record;
      const modalImg = Utils.$('modalImage');
      if (modalImg) modalImg.src = record.url;

      this.prependImages([record]);
      Events.emit('modal:image-open-requested', { record });
      Utils.toast('Edit applied');
    }
  }

  runtime.GalleryController = GalleryController;
})();


